import {
  MAX_VENDOR_PORTAL_UPLOAD_BYTES,
  type VendorPortalUploadTarget,
} from '@erp/rpc';
import { vendorAccountNumberFromFileName } from '@erp/shared-utils';

/** The run choice meaning "attach the file to no run". */
export const NO_RUN = 'none';

/** What the Upload file dialog has collected so far. */
export interface VendorPortalUploadForm {
  workflowId: string | null;
  accountNumber: string;
  /** True once the user typed the account: it is no longer prefilled from the file name. */
  accountEdited: boolean;
  file: File | null;
  /** A workflow run id, or NO_RUN. */
  run: string;
}

export const EMPTY_UPLOAD_FORM: VendorPortalUploadForm = {
  workflowId: null,
  accountNumber: '',
  accountEdited: false,
  file: null,
  run: NO_RUN,
};

/** Pick the vendor: its most recent run by default, and its own file-name rule. */
export function chooseVendor(
  form: VendorPortalUploadForm,
  target: VendorPortalUploadTarget,
): VendorPortalUploadForm {
  return prefillAccount(
    {
      ...form,
      workflowId: target.workflowId,
      run: target.runs[0]?.workflowRunId ?? NO_RUN,
    },
    target,
  );
}

/** Pick the file; `target` is the chosen vendor, if any, for its file-name rule. */
export function chooseFile(
  form: VendorPortalUploadForm,
  file: File | null,
  target: VendorPortalUploadTarget | undefined,
): VendorPortalUploadForm {
  return prefillAccount({ ...form, file }, target);
}

export function editAccount(
  form: VendorPortalUploadForm,
  accountNumber: string,
): VendorPortalUploadForm {
  return { ...form, accountNumber, accountEdited: true };
}

/** The account the list would read from the file name, unless the user typed one. */
function prefillAccount(
  form: VendorPortalUploadForm,
  target: VendorPortalUploadTarget | undefined,
): VendorPortalUploadForm {
  if (form.accountEdited || !form.file) return form;
  return {
    ...form,
    accountNumber:
      vendorAccountNumberFromFileName(
        form.file.name,
        target?.accountNumberFileNamePattern,
      ) ?? '',
  };
}

export interface VendorPortalUploadInput {
  workflowId: string;
  accountNumber: string;
  workflowRunId?: string;
  file: File;
}

/** Why the server would refuse this file before it is sent, or null when it would not. */
export function uploadFileProblem(file: File): 'empty' | 'tooLarge' | null {
  if (file.size === 0) return 'empty';
  if (file.size > MAX_VENDOR_PORTAL_UPLOAD_BYTES) return 'tooLarge';
  return null;
}

/**
 * The upload request, or null until the form has a vendor, an account and a file the
 * server would take.
 */
export function uploadInput(
  form: VendorPortalUploadForm,
): VendorPortalUploadInput | null {
  const accountNumber = form.accountNumber.trim();
  if (!form.workflowId || !form.file || !accountNumber) return null;
  if (uploadFileProblem(form.file)) return null;
  return {
    workflowId: form.workflowId,
    accountNumber,
    ...(form.run === NO_RUN ? {} : { workflowRunId: form.run }),
    file: form.file,
  };
}
